/* eslint-disable react/no-unused-class-component-methods */
import { jsx, css } from '@emotion/react';
import styled from '@emotion/styled';
import loadable from '@loadable/component';
import RJSFForm from '@rjsf/core';
import Cookies from 'js-cookie';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component, createRef, isValidElement } from 'react';
import ReactDOM from 'react-dom';
import ReCAPTCHA from 'react-google-recaptcha';

import LabelWrapper from './LabelWrapper';
import Summary from './MultiStep/Summary';
import TabControls from './MultiStep/TabControls';
import TabsNavigation from './MultiStep/TabsNavigation';
import createMultiStepFormProps from './MultiStep/createMultiStepFormProps';
import OCFields from './OCFields';
import { DefaultTextInput } from './OCInputs';
import RoofingClaimsNextSteps from './RoofingClaimsNextSteps';
import SelectWidget from './SelectWidget';
import { basicFormCSS, hiddenStyle } from './Styles';
import AjaxOverlay from './ajax_overlay';
import { makeCustomValidate } from './customValidate';
import CheckboxesWidget from './fields/CheckboxesWidget';
import DateWidget from './fields/DateWidget';
import DragAndDropFileWidget from './fields/DragAndDropFileWidget';
import EmailCheckWidget from './fields/EmailCheckWidget';
import OnBehalfOfMultiselectWidget from './fields/OnBehalfOfMultiselectWidget';
import PhoneWidget from './fields/PhoneWidget';
import RadioGroup from './fields/RadioGroup';
import RadioWidget from './fields/RadioWidget';
import SplitUsPhone from './fields/SplitUsPhone';
import TextareaWidget from './fields/TextareaWidget';
import YearWidget from './fields/YearWidget';
import SignaturePad from './fields/signaturePad';
import { makeErrorTransformer } from './transformErrors';
import { validator, retrieveSchema } from './validator';
import CmsApi from '../../cms_api';
import { getRequiredSuffix } from '../../helpers/form_libs';
import Api from '../../mdms_api';
import Theme from '../../themes';
import migratePageArrayContents from '../Builder/migrate_old_page_format';
import CTAButton from '../CTAButton';
import Modal from '../Modal';
import Checkbox from '../OC/oc-checkbox';
import { H2, H3 } from '../OC/oc-h';
import RichText from '../OC/oc-rich-text';
import Text from '../OC/oc-text';
import { connectLocation } from '../location/LocationConnectors';

const PRESERVED_RESPONSE_COOKIE_NAME = 'preserved_form_responses';

const getSavedResponses = () => {
  const value = Cookies.get(PRESERVED_RESPONSE_COOKIE_NAME);
  if (value) {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  } else {
    return {};
  }
};

const setSavedResponses = (form_data, propNames) => {
  const savedData = {};
  propNames.forEach(prop => {
    const value = _.get(form_data, prop);
    _.set(savedData, prop, value);
  });
  if (form_data.preserve_responses) {
    Cookies.set(PRESERVED_RESPONSE_COOKIE_NAME, JSON.stringify(savedData));
  } else {
    Cookies.remove(PRESERVED_RESPONSE_COOKIE_NAME);
  }
};

const SectionRenderer = ({ UI, value }) => (
  <div>
    {(value || []).map(module => (
      <ModuleRenderer UI={UI} value={module} />
    ))}
  </div>
);

const ModuleRenderer = ({ UI, value }) => {
  const definition = UI.Modules?.Page?.[value.type];
  const Component = definition?.modes?.view || (() => <div />);
  return (
    <div style={{ paddingTop: '48px' }}>
      <Component value={value?.data} />
    </div>
  );
};

// can remove "NonLibrary" ones once we get rid of server rendering and/or non PB page
const NonLibrarySectionRenderer = ({ value }) => (
  <div>
    {(value || []).map(module => (
      <NonLibraryModuleRenderer value={module} />
    ))}
  </div>
);

const NonLibraryModuleRenderer = ({ value }) => {
  const Component = loadable(props => import(`../OC/PageBuilder/${value.type}`));
  return (
    <div style={{ paddingTop: '48px' }}>
      <Component {...(value?.data || {})} />
    </div>
  );
};

const Consent = () => <Checkbox>Yes</Checkbox>;
const CheckboxWidget = ({ schema, id, value, required, disabled, label, autofocus, onChange }) => (
  <Checkbox
    id={id}
    checked={typeof value === 'undefined' ? false : value}
    required={required}
    disabled={disabled}
    autoFocus={autofocus}
    onChange={event => onChange(!event.target.checked && required ? undefined : event.target.checked)}
  >
    <RichText content={schema?.label || label} inline />
  </Checkbox>
);

export const Recaptcha = connectLocation(({ options, onChange, locale = {} }) => {
  const { code = 'en-US' } = locale;
  return (
    <div style={{ width: 'auto' }}>
      <ReCAPTCHA sitekey={options.recaptchaSiteKey} onChange={onChange} hl={code} />
    </div>
  );
});

const PreserveResponsesCheckbox = props => <CheckboxWidget {...props} />;

const DragAndDropFileWidgetWrapper = connectLocation(props => <DragAndDropFileWidget {...props} />);

const Widgets = {
  BaseInput: DefaultTextInput,
  Consent,
  Recaptcha,
  TextareaWidget,
  DateWidget,
  CheckboxWidget,
  SignaturePad,
  RadioGroup,
  RadioWidget,
  SplitUsPhone,
  SelectWidget,
  CheckboxesWidget,
  EmailCheckWidget,
  DragAndDropFileWidget: DragAndDropFileWidgetWrapper,
  FileWidget: DragAndDropFileWidgetWrapper,
  OnBehalfOfMultiselectWidget,
  PreserveResponsesCheckbox,
  RoofingClaimsNextSteps,
  PhoneWidget,
  YearWidget,
};

const ErrorP = styled.p`
  color: #940420;
  font-size: 12px;
  font-weight: bold;
`;

const LinkError = styled.p`
  display: flex;
  flex-direction: row;
  font-weight: 700;
  align-items: center;
  padding: 16px 24px;
  gap: 9px;
  width: 100%;
  height: 56px;
  background: #f8f8f8;

  @media (min-width: 1200px) {
    width: 34rem;
  }
`;

const RichElementWrapper = styled.div`
  margin-bottom: ${props => (props.margin ? '32px' : '0')};
`;

const RichElement = ({ prop, name, margin }) => (
  <>
    {prop && prop.props[name] && isValidElement(prop) && (
      <RichElementWrapper margin={margin}>
        <RichText content={prop.props[name]} singular />
      </RichElementWrapper>
    )}
    {prop && prop.props[name] && !isValidElement(prop) && (
      <RichElementWrapper margin={margin}>{prop.props[name]}</RichElementWrapper>
    )}
  </>
);

export const CustomFieldTemplate = connectLocation(props => {
  const {
    id,
    classNames,
    label,
    help,
    required,
    uiSchema = {},
    schema,
    description,
    errors,
    rawErrors,
    children,
    hidden,
    formContext,
    t,
  } = props;
  const shouldHideForStep = formContext?.stepProps?.shouldHideForStep({ id, schema });
  const issue = formContext?.stepProps?.getIssueForField({ id });

  const showLabel = label && label.trim() && !uiSchema['ui:options']?.hideLabel;
  const replaceLabel = uiSchema['ui:options']?.replaceTitle || '';
  const isObject = (schema.type || 'object') === 'object';
  const LabelTag = isObject ? (id === 'root' ? H2 : H3) : LabelWrapper;
  const optionalFocused = formContext?.optionalFocused;

  return hidden ? (
    children
  ) : (
    <div className={classNames} css={[shouldHideForStep && hiddenStyle]}>
      {issue && <ErrorP>{issue}</ErrorP>}
      {showLabel && (
        <LabelTag htmlFor={id} prehead={schema?.prehead}>
          <Text content={replaceLabel && replaceLabel !== 'undefined' ? replaceLabel.trim() : label.trim()} />
          {getRequiredSuffix(optionalFocused, required, schema.type)}
        </LabelTag>
      )}
      <RichElement prop={description} name="description" margin={isObject} />
      {children}
      {rawErrors?.map(error => (
        <ErrorP key={error} aria-describedby={id} aria-errormessage className="error" role="alert">
          {error?.trim()}
        </ErrorP>
      ))}
      <RichElement prop={help} name="help" />
    </div>
  );
});

const arrayRemoveButton = css`
  width: 2rem;
  height: 3.45rem;
  background: none;
  cursor: pointer;
  margin-left: auto;
`;

const arrayAddButton = css`
  border-color: #46b8da;
  border-radius: 4px;
  border: 1px solid transparent;
  vertical-align: middle;
  text-align: center;
  line-height: 1.42857143;
  font-weight: 400;
  font-size: 14px;
  margin-bottom: 0;
  padding: 6px 12px;
`;

const arrayInputBox = css`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
`;

const arrayFieldTemplate = css`
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 10px;
`;

const hrMedium = css`
  display: block;
  border: 1px solid #ccc;
  border-width: 0 0 1px;
  margin: 30px 0;
`;

const ArrayFieldTemplate = props => (
  <div css={arrayFieldTemplate}>
    {props.items.map((element, index) => (
      <React.Fragment key={element.key}>
        <div css={arrayInputBox}>
          {element.hasRemove && (
            <button
              className="fa fa-trash"
              type="button"
              css={arrayRemoveButton}
              onClick={element.onDropIndexClick(index)}
            />
          )}
          {element.children}
        </div>
        {!(props.items.length === 1 || index === props.items.length - 1) && <hr css={hrMedium} />}
      </React.Fragment>
    ))}
    {props.canAdd && (
      <button type="button" css={arrayAddButton} onClick={props.onAddClick}>
        + Add
      </button>
    )}
  </div>
);

const ObjectFieldTemplate = ({ properties, schema, uiSchema, formData, formContext }) => (
  <div>
    {properties.map(element => (
      <div key={element.name}>{element.content}</div>
    ))}
    <Summary schema={schema} uiSchema={uiSchema} formData={formData} formContext={formContext} />
  </div>
);

const defaultUiSchema = {
  email: {
    'ui:options': {
      inputType: 'email',
    },
  },
};

class SchemaForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      schema: props.schema,
      uiSchema: props.uiSchema,
      renderSubmit: true,
      formData: props.formData,
      showError: false,
      secure: false,
      serverError: null,
      firstError: null,
      success: !!props.success,
      successContent: props.successContent,
      emailValidationStates: {},
    };
    this.formRef = createRef();
  }

  changeShowError = () => {
    this.setState({ showError: true, serverError: null });
  };

  changeFirstError = selector => {
    this.setState({ firstError: selector, serverError: null });
  };

  setEmailValidationStatus = (fieldId, status, email) => {
    this.setState(prevState => ({
      emailValidationStates: {
        ...prevState.emailValidationStates,
        [fieldId]: { status, email },
      },
    }));
  };

  scrollToErrorInput = errorId => {
    const input = document.getElementById(errorId);
    input.scrollIntoView({ inline: 'start', block: 'center', behavior: 'smooth' });
  };

  focusInputError = () => {
    const initialId = this.state.firstError;
    const secondId = this.state.firstError.split('_').pop();
    const thirdId = `root_${secondId}`;

    if (document.getElementById(initialId)) {
      this.scrollToErrorInput(initialId);
    } else if (document.getElementById(secondId)) {
      this.scrollToErrorInput(secondId);
    } else if (document.getElementById(thirdId)) {
      this.scrollToErrorInput(thirdId);
    } else {
      console.log("Couldn't find an input");
    }
  };

  handleKeypress = e => {
    if (e.key === 'Enter') {
      this.focusInputError();
    }
  };

  get formUrl() {
    return this.props.formUrl || (this.props.uid ? `/api/v2/forms/${this.props.uid}${this.props.search || ''}` : null); // <= this.props.search must render blank when undefined.
  }

  get postUrl() {
    return this.props.postUrl || this.formUrl;
  }

  fetchSchema = () => {
    if (this.requestController) this.requestController.abort();
    if (this.formUrl) {
      this.requestController = new AbortController();
      const method = this.state.secure ? Api.secure_get : Api.get;
      method(this.formUrl, { signal: this.requestController.signal })
        .then(response => {
          if (response.status === 401) {
            if (this.state.secure) {
              // we fail here, the form is available for users only and we aren't authenticated
              console.error("Error! can't load secure form!");
              this.fetchCmsError('401');
            } else {
              // this is a form only available for authenticated users, try again in "secure" mode
              this.setState({ secure: true }, () => {
                this.fetchSchema();
              });
            }
          } else if (response.status === 403) {
            // we fail here, this is a form only available for users with certain permissions
            console.error("Error! can't load secure form!");
            this.fetchCmsError('403');
          } else {
            return response.json();
          }
        })
        .then(result => {
          if (!result) {
            return;
          }
          let schema = result?.schema;
          const prepopulate_default = schema?.meta?.prepopulate_default;
          // if schemas set a default value for preserve_responses, use that
          if (prepopulate_default != undefined) {
            schema.properties.preserve_responses.default = prepopulate_default;
          }
          if (schema?.meta?.prepopulate && schema.meta.prepopulate.length > 0) {
            // set the prepopulated from cookie
            const saved_responses = getSavedResponses();
            schema.meta.prepopulate.forEach(prop => {
              schema = this.setSchemaFromPath(schema, prop, 'default', _.get(saved_responses, prop));
            });
          }
          this.setState(
            {
              schema: result.schema,
              uiSchema: result.ui_schema,
              successContent: result.success_content,
              pbFormName: result.pb_form_name,
            },
            () => {
              this.setAdditionalAttributes();
            }
          );
        })
        .catch(e => {
          if (!this.requestController.signal.aborted) {
            throw e;
          }
        });
    } else if (!this.props.schema) {
      this.setState({ schema: {}, uiSchema: {} });
    }
  };

  getSchemaFromPath = key => {
    const path = key.replace(/\./g, '.properties.').replace(/^\./, '');
    return _.get(this.state.schema, path);
  };

  setSchemaFromPath = (schema, key, field, value) => {
    const path = key.replace(/\./g, '.properties.').replace(/^\./, '');
    return _.set(schema, ['properties', path, field].join('.'), value);
  };

  setAdditionalAttributes = () => {
    const dataTrackFormName =
      this.state.schema?.meta?.['data-track-form-name'] || this.state.pbFormName || this.props.uid;
    const additionalAttributes = {
      'data-track': 'form-submit',
      ...(dataTrackFormName ? { 'data-track-form-name': dataTrackFormName } : {}),
      ...(this.props.additionalAttributes || {}),
      ...this.state.schema?.meta?.formAttributes,
    };

    const element = ReactDOM.findDOMNode(this.formRef.current);
    if (element) {
      element.setAttribute('novalidate', 'novalidate');
      Object.keys(additionalAttributes).forEach(additionalAttribute => {
        element.setAttribute(additionalAttribute, additionalAttributes[additionalAttribute]);
      });
    }
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      this.props.schema !== prevProps.schema ||
      this.props.uiSchema !== prevProps.uiSchema ||
      JSON.stringify(this.props.successContent || {}) !== JSON.stringify(prevProps.successContent || {})
    ) {
      this.setState({
        schema: this.props.schema,
        uiSchema: this.props.uiSchema,
        successContent: this.props.successContent,
        success: this.props.success,
      });
    } else if (this.props.uid !== prevProps.uid || this.props.formUrl !== prevProps.formUrl) {
      this.fetchSchema();
    }
  }

  componentWillUnmount() {
    if (this.requestController) this.requestController.abort();
  }

  componentDidMount() {
    this.fetchSchema();
  }

  successUrl = () => {
    if (this.props.success_content?.type === 'url') {
      return this.props.success_content.url;
    }
    if (this.state.successContent?.type === 'url') {
      return this.state.successContent.url;
    }

    return this.state.schema.meta?.redirectOnSuccess;
  };

  afterSubmit = form_submit => {
    if (this.state.schema?.meta?.prepopulate && this.state.schema?.meta?.prepopulate.length > 0) {
      // set the cookie from form data
      setSavedResponses(form_submit.data_json, this.state.schema?.meta?.prepopulate);
    }
    const redirectUrl = this.successUrl();
    if (redirectUrl) {
      if (this.props.sandbox) {
        alert(`User would be taken to ${redirectUrl}`);
      } else {
        window.location.href = redirectUrl;
      }
      return;
    }
    const isModalOpen =
      !!this.state.schema.meta?.successInModal ||
      (this.state.successContent?.type === 'content' && !!this.state.successContent.modal);
    this.setState({
      success: true,
      isSubmitting: false,
      showError: false,
      serverError: null,
      firstError: null,
      isModalOpen,
    });
    this.props.afterSave && this.props.afterSave(form_submit);
  };

  sandboxFormSubmit = formData => {
    setTimeout(() => {
      // mock data for sandbox
      this.afterSubmit({
        id: 1,
        form_id: 1,
        data: JSON.stringify(formData),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        source: null,
        source_created_at: null,
        data_json: formData,
        user_agent: null,
        request_ip: '127.0.0.1',
        ums_user_id: null,
        ums_user_email: null,
        ums_user_name: null,
        metadata: null,
        status: 'new',
        adobe_id: null,
        retries: 0,
        state_log: null,
        status_log: null,
      });
    }, 1000);
  };

  submit = ({ formData }, _e) => {
    if (this.props.dupeFn && this.props.dupeFn(formData)) {
      this.props.afterSave && this.props.afterSave();
    } else if (
      this.state.schema.meta?.['require-user'] ||
      !_.isEmpty(this.state.schema.meta?.['required-user-permissions'])
    ) {
      this.postFormData(Api.secure_post, formData);
    } else if (this.state.schema.meta?.hideSubmitIf) {
      const { properties } = this.state.schema.meta.hideSubmitIf;
      if (!this.hasPropertiesWithExpectedValues(properties, formData)) {
        this.postFormData(Api.post, formData);
      }
    } else {
      this.postFormData(Api.post, formData);
    }

    return false;
  };

  cleanFormData = data => {
    if (!data || typeof data !== 'object') {
      return data;
    }

    // Filter out null/undefined values from arrays
    if (Array.isArray(data)) {
      return data.filter(item => item !== null && item !== undefined);
    }

    const cleaned = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        cleaned[key] = this.cleanFormData(value);
      }
    });
    return cleaned;
  };

  postFormData(method, formData) {
    this.setState({ isSubmitting: true });
    if (this.props.sandbox) {
      this.sandboxFormSubmit(formData);
    } else {
      method(this.postUrl, formData)
        .then(result => {
          if (result.status >= 200 && result.status < 300) {
            return result.json();
          }
          return result.text().then(response => {
            try {
              console.error({ response: JSON.parse(response) });
            } catch {
              console.error(response);
            }
            throw new Error('There was a server related issue submitting your form.  Please try again later.');
          });
        })
        .then(json => {
          this.afterSubmit(json.result?.form_submit);
        })
        .catch(exception => {
          this.setState({
            success: false,
            isSubmitting: false,
            showError: true,
            serverError: exception.message,
            firstError: null,
          });
        });
    }
  }

  renderResult() {
    if (this.props.UI) {
      return this.state.successContent?.type === 'content' && this.state.successContent?.content ? (
        <SectionRenderer UI={this.props.UI} value={this.state.successContent.content} />
      ) : null;
    }
    // can remove this case once we get rid of server rendering and/or non PB page
    return this.state.successContent?.type === 'content' && this.state.successContent?.content ? (
      <NonLibrarySectionRenderer value={this.state.successContent.content} />
    ) : null;
  }

  fetchCmsError(status) {
    const query = {
      filter: {
        route: `/${status}`,
      },
      fields: {
        '*': 'contents',
      },
    };
    CmsApi.get_single_for_language_contents(query, this.props.userLocale.code || 'en-US', {}).then(result => {
      if (result) {
        this.setState({
          authorizationErrorContent: migratePageArrayContents({
            contents: result,
            metadata: {},
          }).contents.sections[0].modules,
        });
      }
    });
  }

  renderCmsError() {
    if (this.props.UI) {
      return <SectionRenderer UI={this.props.UI} value={this.state.authorizationErrorContent} />;
    }
    // can remove this case once we get rid of server rendering and/or non PB page
    return <NonLibrarySectionRenderer value={this.state.authorizationErrorContent} />;
  }

  hasPropertiesWithExpectedValues(properties, formData = {}) {
    if (Object.keys(properties).length === 0 || Object.keys(formData).length == 0) return false;

    return Object.keys(properties).every(property => {
      if (typeof properties[property] === 'object') {
        return this.hasPropertiesWithExpectedValues(properties[property], formData[property]);
      }
      return formData[property] === properties[property];
    });
  }

  shouldRenderSubmitButton(formData) {
    if (this.state.schema.meta?.hideSubmitIf && formData) {
      const { properties } = this.state.schema.meta.hideSubmitIf;
      if (this.hasPropertiesWithExpectedValues(properties, formData)) {
        return false;
      }
    }

    return true;
  }

  change = ({ formData }, _e) => {
    const cleanedFormData = this.cleanFormData(formData);
    this.setState({
      formData: cleanedFormData,
      renderSubmit: this.shouldRenderSubmitButton(cleanedFormData),
    });
  };

  /**
   * returns the schema but with conditions and $refs resolved based on current form data
   */
  getResolvedSchema = () => retrieveSchema(this.state.schema, this.state.formData);

  get resolvedSchema() {
    return this.getResolvedSchema() || this.state.schema;
  }

  /**
   * runs validation on current form data.
   * does NOT trigger error display
   * essentially a "dry run" of validation
   * returns { errors, schemaErrors }
   */
  validate = () => {
    if (!this.formRef.current?.state?.formData) {
      return { errors: [], schemaErrors: {} };
    }

    const { state, props } = this.formRef.current;
    const { formData, schema, uiSchema } = state;
    const { transformErrors, customValidate } = props;
    return validator.validateFormData(formData, schema, customValidate, transformErrors, uiSchema);
  };

  /**
   * runs validation on current form data.
   * if validation fails it will end up firing onError and displaying errors
   */
  validateShowErrors = () => {
    this.formRef.current?.validateForm();
  };

  /**
   * current RJSF doesn't provide a way to clear out rawErrors programmatically
   * rawErrors are intentionally persisted because async errors can be pushed in there
   * that's what the `extraErrors` prop is about.
   * anyway, unless we implement `onChange`s for every custom field we have to clear out rawErrors
   * the only easy way to do it is force a remount of the form (I know, it's hacky..)
   */
  clearErrors = () => {
    this.setState({ showError: false, serverError: null, firstError: null });
    this.remount();
  };

  remount = () => {
    this.setState({
      key: Date.now(),
    });
  };

  onError = errors => {
    console.log('form validation error(s)', { errors });
    this.changeShowError();
    const error = errors[0].property.replace(/\./g, '_');
    this.changeFirstError(`root${error}`);
  };

  closeModal = () => {
    window.location.reload(true);
  };

  render() {
    if (this.state.authorizationErrorContent) {
      return this.renderCmsError();
    }
    if (!this.state.schema) return null;

    if (this.state.success) {
      return this.renderResult();
    }

    let { children } = this.props;
    const { t, suppressDefaultSubmit } = this.props;
    if (!children && !suppressDefaultSubmit) {
      const cssThemeOverrides = css`
        background: ${Theme.colors.brand};
        border: 1px solid ${Theme.colors.brand};
        font-family: ${Theme.fonts.body};

        &:hover,
        &:focus {
          color: ${Theme.colors.brand};
        }
      `;
      children = (
        <CTAButton
          className="oc-cta-button"
          css={cssThemeOverrides}
          element="button"
          text={t('submit')}
          type="submit"
        />
      );
    }
    const { resolvedSchema } = this;

    const stepProps = createMultiStepFormProps({
      uiSchema: this.state.uiSchema,
      resolvedSchema,
      currentStepIndex: this.state.currentStepIndex,
      setCurrentStepIndex: index => this.setState({ currentStepIndex: index }),
      validate: this.validate,
      validateShowErrors: this.validateShowErrors,
      clearErrors: this.clearErrors,
    });
    const {
      steps,
      isMultiStep,
      isTabbed,
      isFirstStep,
      isLastStep,
      currentStepIndex,
      handleStepSelect,
      handleNextStep,
      handlePreviousStep,
    } = stepProps;

    const templates = {
      ArrayFieldTemplate,
      FieldTemplate: CustomFieldTemplate,
      ObjectFieldTemplate,
    };

    return (
      <AjaxOverlay fetching={this.state.isSubmitting}>
        {isTabbed && <TabsNavigation tabs={steps} activeIndex={currentStepIndex} onSelect={handleStepSelect} />}

        <RJSFForm
          key={this.state.key} // to force remount when key changes
          ref={this.formRef}
          className="rjsfform"
          css={this.props.css || basicFormCSS}
          fields={OCFields}
          formContext={{
            ...(this.state.schema.formContext || {}),
            stepProps,
            setEmailValidationStatus: this.setEmailValidationStatus,
            emailValidationStates: this.state.emailValidationStates,
          }}
          templates={templates}
          onSubmit={this.submit}
          schema={this.state.schema}
          showErrorList={false}
          uiSchema={this.state.uiSchema || defaultUiSchema}
          formData={this.state.formData}
          widgets={Widgets}
          onChange={this.change}
          onError={this.onError}
          validator={validator}
          transformErrors={makeErrorTransformer(this.props.t, resolvedSchema)}
          customValidate={makeCustomValidate(this.props.t, resolvedSchema, () => this.state.emailValidationStates)}
          state={this.state}
          focusInputError={this.focusInputError}
          handleKeypress={this.handleKeypress}
          liveValidate={this.state.showError}
          noHtml5Validate
        >
          {!isMultiStep && this.state.renderSubmit && children}
          {this.state.showError && (
            <div>
              <br />
              {this.state.firstError && (
                <LinkError tabIndex={0} onKeyPress={this.handleKeypress}>
                  <i style={{ color: '#940420', marginRight: '10px' }} className="fa fa-exclamation-triangle fa-lg" />
                  {this.props.t('errors.forms.has_error')}
                  <a style={{ cursor: 'pointer', color: '#940420' }} onClick={this.focusInputError}>
                    {this.props.t('errors.forms.go_to_error')}
                  </a>
                </LinkError>
              )}
              {this.state.serverError && (
                <LinkError tabIndex={0}>
                  <i style={{ color: '#940420', marginRight: '10px' }} className="fa fa-exclamation-triangle fa-lg" />
                  {this.state.serverError}
                </LinkError>
              )}
            </div>
          )}

          {(!isMultiStep || (isMultiStep && isLastStep)) && (
            <>
              {this.state.schema?.meta?.showContactUsText && (
                <div style={{ marginTop: '15px' }}>
                  <RichText content={this.state.schema?.meta?.showContactUsText} />
                </div>
              )}
              {this.state.schema?.meta?.disclaimer && (
                <div style={{ marginTop: '25px' }}>
                  <strong style={{ fontSize: '16px' }}>Disclaimer</strong>
                  <p>{this.state.schema?.meta?.disclaimer}</p>
                </div>
              )}
              {this.state.schema?.meta?.privacyNotice && (
                <div style={{ marginTop: '25px', marginBottom: '25px' }}>
                  <strong style={{ fontSize: '16px' }}>Privacy Notice</strong>
                  <p>{this.state.schema?.meta?.privacyNotice}</p>
                </div>
              )}
              {!this.state.schema?.meta?.hidePrivacyPolicyLink && (
                <div style={{ marginTop: '15px' }}>
                  {this.props.t('privacyPolicyPrefix')}{' '}
                  <a href="/privacy-policy" target="_blank">
                    {this.props.t('privacyPolicy')}
                  </a>
                </div>
              )}
              {this.state.schema?.meta?.footerHtml && (
                <div style={{ marginTop: '25px', fontSize: '11px' }}>
                  <RichText content={this.state.schema?.meta?.footerHtml} />
                </div>
              )}
            </>
          )}

          {isMultiStep && (
            <TabControls
              isFirst={isFirstStep}
              isLast={isLastStep}
              onNext={handleNextStep}
              onPrevious={handlePreviousStep}
              t={this.props.t}
            />
          )}
          <Modal open={this.state.isModalOpen} onClose={this.closeModal}>
            {this.renderResult()}
          </Modal>
        </RJSFForm>
      </AjaxOverlay>
    );
  }
}
SchemaForm.propTypes = {
  t: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  children: PropTypes.node,
  uid: PropTypes.string,
  json: PropTypes.string,
  formUrl: PropTypes.string,
  postUrl: PropTypes.string,
  css: PropTypes.object,
  // if true, only fakes a submission (for WYSIWYG builder or test mode)
  sandbox: PropTypes.bool,
  // override to show success state of form, typically not passed to let form control its own state
  success: PropTypes.bool,
  // pass true to omit rendering a default "submit" button
  suppressDefaultSubmit: PropTypes.bool,
};

SchemaForm.defaultProps = {
  suppressDefaultSubmit: false,
};

SchemaForm = connectLocation(SchemaForm);

export { SchemaForm };
